// Aquarium mingle steering: stand still or walk toward another free NPC.
import type { RuntimeNpc } from '../types.js';

const APPROACH_PX_S = 36;

function steerAt(self: RuntimeNpc, target: RuntimeNpc, retargetMs: number): { vx: number; vy: number; retargetMs: number } {
  const dx = target.x - self.x;
  const dy = target.y - self.y;
  const d = Math.hypot(dx, dy) || 1;
  return { vx: (dx / d) * APPROACH_PX_S, vy: (dy / d) * APPROACH_PX_S, retargetMs };
}

/** Walk straight at a target at mingle approach speed. */
export function steerToward(
  self: RuntimeNpc,
  target: { x: number; y: number },
  retargetMs: number,
): { vx: number; vy: number; retargetMs: number } {
  const dx = target.x - self.x;
  const dy = target.y - self.y;
  const d = Math.hypot(dx, dy) || 1;
  return { vx: (dx / d) * APPROACH_PX_S, vy: (dy / d) * APPROACH_PX_S, retargetMs };
}

/** During a room break: pause briefly or wander to a random floor point. */
export function breakSteer(
  self: RuntimeNpc,
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
  pauseRoll: number,
  destRollX: number,
  destRollY: number,
): { vx: number; vy: number; retargetMs: number } {
  if (pauseRoll < 0.35) {
    return { vx: 0, vy: 0, retargetMs: 1500 + Math.round(destRollX * 2500) };
  }
  const destX = bounds.minX + destRollX * (bounds.maxX - bounds.minX);
  const destY = bounds.minY + destRollY * (bounds.maxY - bounds.minY);
  const dx = destX - self.x;
  const dy = destY - self.y;
  const d = Math.hypot(dx, dy);
  if (d <= 8) {
    return { vx: 0, vy: 0, retargetMs: 1500 };
  }
  return {
    vx: (dx / d) * APPROACH_PX_S,
    vy: (dy / d) * APPROACH_PX_S,
    retargetMs: 2000 + Math.round(destRollY * 3000),
  };
}

/** Keep closing on the nearest free person once a walk has started. */
export function mingleSteer(
  self: RuntimeNpc,
  others: RuntimeNpc[],
  roll: number,
  pickIndex: number,
  encounterDist: number,
): { vx: number; vy: number; retargetMs: number } {
  const free = others.filter((o) => o.npcId !== self.npcId && !o.busyWith);
  let nearest: RuntimeNpc | null = null;
  let best = Infinity;
  for (const o of free) {
    const d = Math.hypot(o.x - self.x, o.y - self.y);
    if (d < best) {
      best = d;
      nearest = o;
    }
  }
  const walking = Math.hypot(self.vx, self.vy) > 0.01;
  if (walking && nearest && best > encounterDist) return steerAt(self, nearest, 400);
  const rolled = mingleVelocity(self, others, roll, pickIndex);
  if (rolled.vx === 0 && rolled.vy === 0) return rolled;
  const len = Math.hypot(rolled.vx, rolled.vy) || 1;
  return {
    vx: (rolled.vx / len) * APPROACH_PX_S,
    vy: (rolled.vy / len) * APPROACH_PX_S,
    retargetMs: rolled.retargetMs,
  };
}

export function mingleVelocity(
  self: RuntimeNpc,
  others: RuntimeNpc[],
  roll: number,
  pickIndex: number,
): { vx: number; vy: number; retargetMs: number } {
  if (roll < 0.3) {
    return { vx: 0, vy: 0, retargetMs: 2500 };
  }
  const free = others.filter((o) => o.npcId !== self.npcId && !o.busyWith);
  if (free.length === 0) {
    return { vx: 0, vy: 0, retargetMs: 2500 };
  }
  const target = free[pickIndex % free.length];
  const dx = target.x - self.x;
  const dy = target.y - self.y;
  const d = Math.hypot(dx, dy);
  if (d <= 1) {
    return { vx: 0, vy: 0, retargetMs: 2500 };
  }
  const sp = self.speedPxS || 12;
  return { vx: (dx / d) * sp, vy: (dy / d) * sp, retargetMs: 800 };
}
